import { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { trpc } from '../lib/trpc';
import type { ManagedPlanCode } from '@shared/config/subscription-plans';
import { logger } from '../utils/logger';

export interface CreateSubscriptionParams {
  planCode: string;
  predictionAddonQuantity?: number;
  userAddonQuantity?: number;
  agentCode?: string;
  promotionCode?: string;
  // プレビューで作成済みの顧客ID（重複作成を防ぐ）
  existingCustomerId?: string;
}

export interface CreateSubscriptionResult {
  subscriptionId: string;
  invoiceId: string;
  prorationAmount: number;
  totalAmount: number;
  success: boolean;
}

export function useStripeSubscription() {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createSetupIntent = trpc.stripe.createSetupIntent.useMutation();
  const createSubscription = trpc.stripe.createSubscriptionWithProration.useMutation();
  const utils = trpc.useUtils();

  const createSubscriptionWithCard = async (
    params: CreateSubscriptionParams
  ): Promise<CreateSubscriptionResult> => {
    if (!stripe || !elements) {
      throw new Error('Stripeが初期化されていません');
    }

    setLoading(true);
    setError(null);

    try {
      // mutationをリセット（古いSetupIntentを使わないようにする）
      createSetupIntent.reset();

      // ステップ1: Setup Intent作成（プレビューで作成済みの顧客IDがあれば使用）
      const { clientSecret, customerId } = await createSetupIntent.mutateAsync({
        existingCustomerId: params.existingCustomerId,
      });

      logger.info('SetupIntent created:', {
        clientSecret: `${clientSecret.substring(0, 20)}...`,
        customerId,
      });

      // ステップ2: カード情報を登録
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('カード入力フォームが見つかりません');
      }

      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (setupError) {
        throw new Error(setupError.message || 'カード登録に失敗しました');
      }

      if (!setupIntent?.payment_method) {
        throw new Error('支払い方法の登録に失敗しました');
      }

      // ステップ3: Subscription作成（日割り計算付き）
      const result = await createSubscription.mutateAsync({
        planCode: params.planCode as ManagedPlanCode,
        paymentMethodId: setupIntent.payment_method as string,
        customerId,
        predictionAddonQuantity: params.predictionAddonQuantity,
        userAddonQuantity: params.userAddonQuantity,
        agentCode: params.agentCode,
        promotionCode: params.promotionCode,
      });

      // ステップ4: Payment Intentを確認（3D Secureなど）
      if (result.clientSecret) {
        const { paymentIntent, error: paymentError } = await stripe.confirmCardPayment(
          result.clientSecret
        );

        if (paymentError) {
          throw new Error(paymentError.message || '決済に失敗しました');
        }

        if (paymentIntent?.status !== 'succeeded') {
          throw new Error('決済が完了しませんでした');
        }
      }

      // サブスクリプション作成成功後、即座にキャッシュを無効化
      void utils.company.getCurrentCompany.invalidate();
      void utils.company.getCompanyInfo.invalidate();
      void utils.freeTrial.getLimitations.invalidate();
      void utils.freeTrial.getUpgradeInfo.invalidate();

      setLoading(false);
      return {
        ...result,
        success: true,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '予期しないエラーが発生しました';
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  };

  return {
    createSubscriptionWithCard,
    loading,
    error,
  };
}
